'use client'

import * as React from 'react'
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

import { useIsMobile } from '../hooks/use-mobile'
import { cn } from '../lib/utils'
import { Button } from './button'
import { Input } from './input'
import { Separator } from './separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './sheet'
import { Skeleton } from './skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'
import { ChevronRightIcon, PanelLeftIcon } from 'lucide-react'

const SIDEBAR_COOKIE_NAME = 'sidebar_state'
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = '18rem'
const SIDEBAR_WIDTH_MOBILE = '18rem'
const SIDEBAR_WIDTH_ICON = '3rem'
const SIDEBAR_KEYBOARD_SHORTCUT = 'b'

// Resizable sidebar bounds. Width is stored in pixels (the resizer drags in
// pixels) and clamped to this range. Drag below SIDEBAR_COLLAPSE_AT_PX and the
// sidebar snaps closed instead of clamping at min width — Slack / Notion /
// Linear behaviour, feels obvious in use.
//
// ⭐ The GAP between MIN and COLLAPSE_AT is the whole feel of the drag, and it
// has to be small. It is dead travel: once you hit MIN the sidebar stops moving,
// but nothing else happens until the pointer passes COLLAPSE_AT. It used to be
// 208 and 168, so there were 40px of dragging against a sidebar that had already
// stopped responding before it finally shut — which reads as "it won't close",
// not as resistance. 20px is enough to stop an accidental close and short enough
// that the snap follows the hand.
//
// COLLAPSE_AT must stay BELOW MIN: invert them and the sidebar closes before it
// can ever reach its own minimum, making MIN unreachable by drag.
const SIDEBAR_WIDTH_COOKIE_NAME = 'sidebar_width'
const SIDEBAR_WIDTH_DEFAULT_PX = 288 // 18rem matches the legacy SIDEBAR_WIDTH
const SIDEBAR_WIDTH_MIN_PX = 240 // the floor — below this the nav rows crowd
const SIDEBAR_WIDTH_MAX_PX = 480 // 30rem — anything wider eats too much canvas
const SIDEBAR_COLLAPSE_AT_PX = 220 // 20px under MIN: drag past it and it closes

type SidebarContextProps = {
  state: 'expanded' | 'collapsed'
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  /** Current sidebar width in pixels (desktop only — ignored on mobile). */
  width: number
  /** Set the sidebar width, clamped to [MIN, MAX] and persisted in cookie. */
  setWidth: (n: number) => void
  /** True while the user is actively dragging the resizer. */
  resizing: boolean
  setResizing: (r: boolean) => void
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.')
  }

  return context
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const isMobile = useIsMobile()
  const [openMobile, setOpenMobile] = React.useState(false)

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen)
  const open = openProp ?? _open
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value
      if (setOpenProp) {
        setOpenProp(openState)
      } else {
        _setOpen(openState)
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    },
    [setOpenProp, open]
  )

  // Width state — read once on mount from cookie so the sidebar opens at
  // whatever the operator last sized it to. Persisted on every change so
  // a quick drag keeps without needing to hold the mouse longer.
  const [width, setWidthState] = React.useState<number>(
    SIDEBAR_WIDTH_DEFAULT_PX
  )
  React.useEffect(() => {
    if (typeof document === 'undefined') return
    const match = document.cookie.match(
      new RegExp(`${SIDEBAR_WIDTH_COOKIE_NAME}=(\\d+)`)
    )
    if (!match) return
    const n = parseInt(match[1], 10)
    if (!Number.isFinite(n)) return
    setWidthState(
      Math.max(SIDEBAR_WIDTH_MIN_PX, Math.min(SIDEBAR_WIDTH_MAX_PX, n))
    )
  }, [])
  const setWidth = React.useCallback((n: number) => {
    const clamped = Math.max(
      SIDEBAR_WIDTH_MIN_PX,
      Math.min(SIDEBAR_WIDTH_MAX_PX, n)
    )
    setWidthState(clamped)
    if (typeof document !== 'undefined') {
      document.cookie = `${SIDEBAR_WIDTH_COOKIE_NAME}=${clamped}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    }
  }, [])
  const [resizing, setResizing] = React.useState(false)

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open)
  }, [isMobile, setOpen, setOpenMobile])

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        toggleSidebar()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleSidebar])

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? 'expanded' : 'collapsed'

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      width,
      setWidth,
      resizing,
      setResizing,
    }),
    [
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      width,
      setWidth,
      resizing,
    ]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        data-slot="sidebar-wrapper"
        // While dragging the resizer we kill all width transitions on
        // descendants (sidebar-gap, sidebar-container) so the drag tracks
        // the pointer 1:1 instead of easing 200ms behind it. Also disable
        // text selection — without this the cursor flips to text-select
        // every time the pointer crosses a span/label.
        data-resizing={resizing ? '' : undefined}
        style={
          {
            '--sidebar-width': `${width}px`,
            '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          'group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar data-[resizing]:select-none [&[data-resizing]_*]:!transition-none',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  )
}

function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  dir,
  ...props
}: React.ComponentProps<'div'> & {
  side?: 'left' | 'right'
  variant?: 'sidebar' | 'floating' | 'inset'
  collapsible?: 'offcanvas' | 'icon' | 'none'
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar()

  if (collapsible === 'none') {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          'flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          dir={dir}
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <div
      className="group peer hidden text-sidebar-foreground md:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          'relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
        )}
      />
      <div
        data-slot="sidebar-container"
        data-side={side}
        className={cn(
          'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear data-[side=left]:left-0 data-[side=left]:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)] data-[side=right]:right-0 data-[side=right]:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)] md:flex',
          // Adjust the padding for floating and inset variants.
          variant === 'floating' || variant === 'inset'
            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l border-sidebar-border',
          className
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="flex size-full flex-col bg-sidebar group-data-[variant=floating]:rounded-2xl group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1 group-data-[variant=floating]:ring-sidebar-border"
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar()

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon-sm"
      className={cn(className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

/**
 * Drag-to-resize handle on the sidebar's inner edge. Drag horizontally to
 * change `--sidebar-width`. Drag below `SIDEBAR_COLLAPSE_AT_PX` and the
 * sidebar snaps closed (the prior width persists, so toggling open
 * restores it). A click without movement falls through to toggling the
 * sidebar — keeps the legacy SidebarRail UX even with the resizer in
 * place. Hidden on mobile (the mobile sidebar uses Sheet).
 */
// Style ids the resizer (and the DnD code in app pages) inject. Tracked
// in one place so we can defensively remove orphans on mount in case a
// previous drag was interrupted (window blur, pointer released outside
// the viewport, navigation) and the cleanup never ran. Without this
// recovery the page-wide `* { cursor: …!important }` rule stays
// attached, breaking every other cursor (sidebar resize, booking-pill
// resize, drag handles, etc.) until the user reloads.
const ORPHAN_CURSOR_STYLE_IDS = ['sidebar-resize-cursor', 'dnd-grabbing-cursor']

function clearOrphanCursorStyles() {
  if (typeof document === 'undefined') return
  for (const id of ORPHAN_CURSOR_STYLE_IDS) {
    document.getElementById(id)?.remove()
  }
}

// Module-scoped one-shot style injection. The resizer's resting cursor
// (`col-resize`) and zero radius need to win against:
//   - Tailwind preflight's `button { cursor: pointer }` reset
//   - any consuming app's own button-rounding rules (e.g. connect's
//     `.new-ui-theme button { border-radius: 9999px }` pill rule)
// Inline style on the element doesn't reliably win in every consumer's
// CSS pipeline, so we inject a tightly-scoped `[data-slot='sidebar-
// resizer']` rule with `!important` once per page load. Owned by the
// package so consumers don't have to duplicate it.
const RESIZER_STYLE_ID = 'tailwind-v4-sidebar-resizer-style'
function ensureResizerStyle() {
  if (typeof document === 'undefined') return
  if (document.getElementById(RESIZER_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = RESIZER_STYLE_ID
  // Doubled selector bumps specificity to (0,2,0) so even other
  // attribute-selector rules with !important struggle to outrank it.
  style.textContent = `[data-slot='sidebar-resizer'][data-slot='sidebar-resizer']{cursor:col-resize!important;border-radius:0!important;}`
  document.head.appendChild(style)
}

function SidebarResizer({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  const {
    isMobile,
    open,
    setOpen,
    width,
    setWidth,
    setResizing,
    toggleSidebar,
  } = useSidebar()
  const buttonRef = React.useRef<HTMLDivElement | null>(null)

  // Defensive cleanup of any stale full-page cursor-override styles —
  // both ours and the DnD ones in app pages — on mount, on unmount,
  // and when the document regains visibility (covers the
  // window-blur-during-drag case explicitly). Also injects the
  // resting-cursor style if it isn't already attached, and pins the
  // button's own cursor with `!important` via setProperty (the React
  // `style` prop can't set !important, and a non-important inline
  // style was being outranked by Tailwind preflight in some app
  // builds).
  React.useEffect(() => {
    ensureResizerStyle()
    clearOrphanCursorStyles()
    buttonRef.current?.style.setProperty('cursor', 'col-resize', 'important')
    const onVisible = () => {
      if (document.visibilityState === 'visible') clearOrphanCursorStyles()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearOrphanCursorStyles()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const startDrag = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (isMobile) return
      e.preventDefault()
      e.stopPropagation()
      const startX = e.clientX
      const startWidth = open ? width : 0
      let movement = 0
      let collapsedDuringDrag = !open

      setResizing(true)
      // Pin the cursor on the document so it stays as col-resize even
      // when the pointer crosses Switches/buttons that have their own
      // cursor — same trick we use for DnD.
      const styleEl = document.createElement('style')
      styleEl.id = 'sidebar-resize-cursor'
      styleEl.textContent =
        '*, *::before, *::after { cursor: col-resize !important; }'
      document.head.appendChild(styleEl)

      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientX - startX
        movement = Math.max(movement, Math.abs(delta))
        const proposed = startWidth + delta
        if (proposed < SIDEBAR_COLLAPSE_AT_PX) {
          // Dragged too narrow — snap closed but keep the last open
          // width so re-opening restores it.
          if (!collapsedDuringDrag) {
            collapsedDuringDrag = true
            setOpen(false)
          }
        } else {
          if (collapsedDuringDrag) {
            collapsedDuringDrag = false
            setOpen(true)
          }
          setWidth(proposed)
        }
      }

      const cleanup = () => {
        setResizing(false)
        document.getElementById('sidebar-resize-cursor')?.remove()
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
        window.removeEventListener('blur', onUp)
      }

      const onUp = () => {
        cleanup()
        // Click-without-drag toggles the sidebar — operators expect to
        // be able to tap the rail to collapse/expand without having to
        // drag all the way past the collapse threshold.
        if (movement < 4) toggleSidebar()
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
      // Also bail out if the window loses focus mid-drag so a tab
      // switch / Cmd-Tab doesn't leave the global cursor style stuck
      // on every element until reload.
      window.addEventListener('blur', onUp)
    },
    [isMobile, open, width, setOpen, setWidth, setResizing, toggleSidebar]
  )

  return (
    <div
      ref={buttonRef}
      role="separator"
      aria-orientation="vertical"
      data-sidebar="resizer"
      data-slot="sidebar-resizer"
      aria-label="Resize sidebar"
      tabIndex={-1}
      onPointerDown={startDrag}
      title="Drag to resize, click to toggle"
      // Rendered as <div role="separator"> rather than <button> because
      // app-level CSS in some consuming projects forces `cursor: pointer`
      // on every <button> / [role='button'] (e.g. connect's
      // `index.css`). A neutral div with the splitter ARIA role
      // sidesteps both selectors, lets our `cursor: col-resize` actually
      // land, and is the semantically correct affordance for a resize
      // splitter anyway.
      // Cursor is also pinned via `setProperty(..., 'important')` in
      // the mount effect, the Tailwind utility class below, and a
      // module-injected `<style>`. `touchAction: 'none'` stops the
      // browser's swipe-back gesture eating trackpad drags.
      style={{ cursor: 'col-resize', touchAction: 'none' }}
      className={cn(
        // Wider hit zone (24px) centred on the inner edge so the
        // operator doesn't have to pixel-hunt. Always-visible 1px divider
        // on the inside uses the `--border` token — the single
        // product-wide border — so the sidebar edge reads at exactly the
        // same weight as every other rule; the grip pill carries
        // discoverability, and it only darkens on hover/drag (no width
        // change) so the affordance responds without a heavy dark bar
        // jumping out against the subtle resting line.
        'cursor-col-resize',
        'absolute inset-y-0 z-[60] hidden w-6 sm:block',
        'group-data-[side=left]:-right-3 group-data-[side=right]:-left-3',
        // ⚠️ The resizer no longer paints the RESTING seam — the Sidebar
        // container's own `border-r` does, and it is the only thing that should.
        //
        // This used to draw a 1px `bg-border` line here, centred on the rail via
        // `start-1/2 -translate-x-1/2`. That puts it on a half-pixel offset from
        // a container edge that is itself only whole-numbered by luck: the
        // sidebar is drag-resizable, so its width is whatever the operator left
        // it at. Land on a fraction and the browser spreads one hairline across
        // two device pixels at half alpha each, which reads as a soft doubled
        // band rather than a rule — and it only reproduces for whoever has that
        // width persisted in their `sidebar_width` cookie, which is why it was
        // invisible to everyone else. An element BORDER cannot do that; browsers
        // snap those to the device pixel grid.
        //
        // What is left here is the affordance only: transparent at rest, colour
        // on hover and while dragging. Consumers must therefore NOT disable the
        // container border (`group-data-[side=left]:border-r-0`) or they get no
        // seam at all.
        'after:absolute after:inset-y-0 after:start-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent after:transition-colors',
        // Hover / drag: the line appears, at the interactive weight.
        'hover:after:bg-[var(--foreground)]/20 data-[resizing]:after:bg-[var(--foreground)]/20',
        // Collapsed (offcanvas) sidebar — there's nothing to resize.
        'group-data-[collapsible=offcanvas]/sidebar-wrapper:hidden',
        className
      )}
      {...props}
    >
      {/* Pill "grabber" handle centred on the line — a rounded pill filled
          with a fine dotted texture (currentColor dots, so it adapts to the
          theme) so it reads as a draggable grip. */}
      <span
        aria-hidden
        data-slot="sidebar-resizer-grip"
        className="pointer-events-none absolute start-1/2 top-1/2 z-10 h-8 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[var(--foreground)]/15 bg-[var(--background)] text-[var(--foreground)]/20 [background-image:radial-gradient(currentColor_0.5px,transparent_0.5px)] [background-size:3px_3px]"
      />
      {children}
    </div>
  )
}

function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        'absolute inset-y-0 z-20 hidden w-4 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:start-1/2 after:w-[2px] hover:after:bg-sidebar-border sm:flex ltr:-translate-x-1/2 rtl:-translate-x-1/2',
        'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-sidebar',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className
      )}
      {...props}
    />
  )
}

function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        'relative flex w-full flex-1 flex-col bg-background md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2',
        className
      )}
      {...props}
    />
  )
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn('h-8 w-full bg-background shadow-none', className)}
      {...props}
    />
  )
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  )
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  )
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn('mx-2 w-auto bg-sidebar-border', className)}
      {...props}
    />
  )
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        'no-scrollbar flex min-h-0 flex-1 flex-col gap-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className
      )}
      {...props}
    />
  )
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      {...props}
    />
  )
}

function SidebarGroupLabel({
  className,
  render,
  ...props
}: useRender.ComponentProps<'div'> & React.ComponentProps<'div'>) {
  return useRender({
    defaultTagName: 'div',
    props: mergeProps<'div'>(
      {
        className: cn(
          'flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 ring-sidebar-ring outline-hidden transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
          className
        ),
      },
      props
    ),
    render,
    state: {
      slot: 'sidebar-group-label',
      sidebar: 'group-label',
    },
  })
}

function SidebarGroupAction({
  className,
  render,
  ...props
}: useRender.ComponentProps<'button'> & React.ComponentProps<'button'>) {
  return useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(
      {
        className: cn(
          'absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0',
          className
        ),
      },
      props
    ),
    render,
    state: {
      slot: 'sidebar-group-action',
      sidebar: 'group-action',
    },
  })
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn('w-full text-sm', className)}
      {...props}
    />
  )
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn(
        // 1px between rows so adjacent hover/active pills don't touch.
        // Collapsed to icon-only there's no gap — the squares stack flush.
        'flex w-full min-w-0 flex-col gap-[1px] group-data-[collapsible=icon]:gap-0',
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn('group/menu-item relative', className)}
      {...props}
    />
  )
}

const sidebarMenuButtonVariants = cva(
  // `rounded-full` so the row's hover/active bg follows the pill
  // language used everywhere else (Button, form fields). Was
  // `rounded-md`, which left the hover highlight with square-ish
  // corners and looked off against the rest of the new UI.
  // Row spacing (gap-3 icon↔label, px-3 py-2, auto height) is the
  // canonical look — matches the new-UI nav across every app so the
  // sidebar is identical wherever it's imported, no per-consumer
  // overrides needed. Icon-only collapsed mode resets to a 32px square
  // (size-8! / p-2!) so the !important wins over the px-3 py-2 base.
  'peer/menu-button group/menu-button flex w-full items-center gap-3 overflow-hidden rounded-full px-3 py-2 text-left text-sm ring-sidebar-ring outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:font-medium data-active:text-sidebar-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground data-active:[&_svg]:text-sidebar-accent-foreground [&>span:last-child]:truncate',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline:
          'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
      size: {
        // Auto height + the base px-3 py-2 gives the canonical ~36px row
        // (matches connect's nav). Was a fixed h-8; auto lets longer
        // labels / multi-line footer rows breathe without per-row tuning.
        default: 'h-auto text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function SidebarMenuButton({
  render,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: useRender.ComponentProps<'button'> &
  React.ComponentProps<'button'> & {
    isActive?: boolean
    tooltip?: string | React.ComponentProps<typeof TooltipContent>
  } & VariantProps<typeof sidebarMenuButtonVariants>) {
  const { isMobile, state } = useSidebar()
  const comp = useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(
      {
        className: cn(sidebarMenuButtonVariants({ variant, size }), className),
      },
      props
    ),
    render: !tooltip ? render : <TooltipTrigger render={render} />,
    state: {
      slot: 'sidebar-menu-button',
      sidebar: 'menu-button',
      size,
      active: isActive,
    },
  })

  if (!tooltip) {
    return comp
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    }
  }

  return (
    <Tooltip>
      {comp}
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        {...tooltip}
      />
    </Tooltip>
  )
}

function SidebarMenuAction({
  className,
  render,
  showOnHover = false,
  ...props
}: useRender.ComponentProps<'button'> &
  React.ComponentProps<'button'> & {
    showOnHover?: boolean
  }) {
  return useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(
      {
        className: cn(
          'absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0',
          showOnHover &&
            'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-active/menu-button:text-sidebar-accent-foreground aria-expanded:opacity-100 md:opacity-0',
          className
        ),
      },
      props
    ),
    render,
    state: {
      slot: 'sidebar-menu-action',
      sidebar: 'menu-action',
    },
  })
}

/**
 * Trailing chevron for SidebarMenuButtons that drill into a nested
 * section. Renders as a flush icon at rest and grows a circular
 * `--foreground/10` puck on hover (driven by the parent button's
 * `group/menu-button` class so the whole row triggers the highlight).
 *
 *   <SidebarMenuButton ...>
 *     <Icon /> Label
 *     <SidebarMenuChevron />
 *   </SidebarMenuButton>
 *
 * Use only when the row links into a child list — the puck signals
 * "press to drill in", parallel to base-ui's accordion / disclosure
 * affordances.
 */
function SidebarMenuChevron({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      aria-hidden
      data-slot="sidebar-menu-chevron"
      className={cn(
        'ml-auto flex size-5 items-center justify-center rounded-full transition-colors group-hover/menu-button:bg-[var(--foreground)]/5 group-data-active/menu-button:bg-[var(--foreground)]/5',
        className
      )}
      {...props}
    >
      <ChevronRightIcon strokeWidth={2.5} className="size-3.5 opacity-60" />
    </span>
  )
}

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        'pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-sidebar-foreground tabular-nums select-none group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 peer-data-active/menu-button:text-sidebar-accent-foreground',
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean
}) {
  // Random width between 50 to 90%.
  const [width] = React.useState(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`
  })

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  )
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5 group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn('group/menu-sub-item relative', className)}
      {...props}
    />
  )
}

function SidebarMenuSubButton({
  render,
  size = 'md',
  isActive = false,
  className,
  ...props
}: useRender.ComponentProps<'a'> &
  React.ComponentProps<'a'> & {
    size?: 'sm' | 'md'
    isActive?: boolean
  }) {
  return useRender({
    defaultTagName: 'a',
    props: mergeProps<'a'>(
      {
        className: cn(
          'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-full px-3 text-sidebar-foreground ring-sidebar-ring outline-hidden group-data-[collapsible=icon]:hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[size=md]:text-sm data-[size=sm]:text-xs data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground',
          className
        ),
      },
      props
    ),
    render,
    state: {
      slot: 'sidebar-menu-sub-button',
      sidebar: 'menu-sub-button',
      size,
      active: isActive,
    },
  })
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuChevron,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarResizer,
  SidebarSeparator,
  SidebarTrigger,
  sidebarMenuButtonVariants,
  useSidebar,
}
